// =====================
// Deployments Module Types
// =====================
// Per-project deployment planning + tracking. Gated by the project feature
// toggle `settings.features.deployments` (off by default). A deployment's
// scope is the union of PLANNED items (attached while planning) and DETECTED
// items (derived from the GitHub compare range fromSha..toSha).

export const DEPLOYMENT_STATUSES = [
  'PLANNED',
  'IN_PROGRESS',
  'READY',
  'DEPLOYING',
  'DEPLOYED',
  'FAILED',
  'ROLLED_BACK',
  'CANCELLED',
] as const

export type DeploymentStatus = (typeof DEPLOYMENT_STATUSES)[number]

// Terminal statuses lock the deployment scope; deployedAt is only set for
// DEPLOYED
export const TERMINAL_DEPLOYMENT_STATUSES: DeploymentStatus[] = [
  'DEPLOYED',
  'FAILED',
  'ROLLED_BACK',
  'CANCELLED',
]

export const DEPLOYMENT_STATUS_LABELS: Record<DeploymentStatus, string> = {
  PLANNED: 'Planned',
  IN_PROGRESS: 'In progress',
  READY: 'Ready',
  DEPLOYING: 'Deploying',
  DEPLOYED: 'Deployed',
  FAILED: 'Failed',
  ROLLED_BACK: 'Rolled back',
  CANCELLED: 'Cancelled',
}

export interface ProjectEnvironment {
  id: string
  organizationId: string
  projectId: string
  name: string
  key: string
  color?: string | null
  url?: string | null
  // Git branch this environment tracks (e.g. prod -> "main"); default target
  // ref for deployments/promotions into this environment
  branchName?: string | null
  isProduction: boolean
  sortOrder: number
  isArchived: boolean
  createdAt: string
  updatedAt: string
}

// Environments seeded the first time the deployments API is used on a
// project with the feature enabled
export const DEFAULT_PROJECT_ENVIRONMENTS: Array<
  Pick<ProjectEnvironment, 'name' | 'key' | 'isProduction' | 'sortOrder'> & {
    color: string
  }
> = [
  { name: 'Development', key: 'dev', color: 'blue', isProduction: false, sortOrder: 0 },
  { name: 'Staging', key: 'staging', color: 'amber', isProduction: false, sortOrder: 1 },
  { name: 'Production', key: 'prod', color: 'green', isProduction: true, sortOrder: 2 },
]

export interface DeploymentChecklistItem {
  id: string
  label: string
  done: boolean
  doneBy?: string | null
  doneAt?: string | null
}

export interface DeploymentDetectionSummary {
  commitCount?: number
  comparedAt?: string
  // Commits in the range that could not be resolved to a PR or task
  unmatchedCommits?: Array<{
    sha: string
    message: string
    authorLogin?: string | null
  }>
  error?: string | null
}

export interface DeploymentItem {
  id: string
  organizationId: string
  deploymentId: string
  taskId?: string | null
  ticketId?: string | null
  isPlanned: boolean
  isDetected: boolean
  repositoryId?: string | null
  pullRequestNumber?: number | null
  pullRequestTitle?: string | null
  pullRequestUrl?: string | null
  mergeCommitSha?: string | null
  metadata?: Record<string, unknown>
  addedBy?: string | null
  createdAt: string
  updatedAt: string
  // Hydrated on read
  task?: {
    id: string
    name: string
    taskNumber?: string | null
    statusId?: string | null
    status?: { id: string; name: string; color?: string | null } | null
    assigneeId?: string | null
  } | null
  ticket?: {
    id: string
    subject: string
    ticketNumber?: string | null
    stage?: string | null
  } | null
}

export interface Deployment {
  id: string
  organizationId: string
  projectId: string
  environmentId: string
  name: string
  version?: string | null
  description?: string | null
  status: DeploymentStatus
  scheduledFor?: string | null
  startedAt?: string | null
  deployedAt?: string | null
  ownerId?: string | null
  repositoryId?: string | null
  fromSha?: string | null
  toSha?: string | null
  targetRef?: string | null
  compareUrl?: string | null
  detectionSummary?: DeploymentDetectionSummary
  lastDetectedAt?: string | null
  releaseNotes?: string | null
  showInPortal: boolean
  checklist: DeploymentChecklistItem[]
  metadata?: Record<string, unknown>
  promotedFromId?: string | null
  createdBy?: string | null
  createdAt: string
  updatedAt: string
  // Hydrated on read
  environment?: ProjectEnvironment
  repository?: {
    id: string
    fullName: string
    htmlUrl?: string | null
    defaultBranch?: string
  } | null
  items?: DeploymentItem[]
  itemCounts?: { planned: number; detected: number; total: number }
  // Promotion lineage, hydrated on detail reads: where this deployment came
  // from and where it has been promoted to
  promotedFrom?: {
    id: string
    name: string
    version?: string | null
    status: DeploymentStatus
    environment?: { id: string; name: string; key: string; color?: string | null } | null
  } | null
  promotions?: Array<{
    id: string
    name: string
    version?: string | null
    status: DeploymentStatus
    environment?: { id: string; name: string; key: string; color?: string | null } | null
  }>
}

// ---- List query params (server-side pagination / filtering / sorting) ----

// Whitelist of deployment scalar columns the API will sort by.
export const DEPLOYMENT_SORT_FIELDS = [
  'name',
  'version',
  'status',
  'environmentId',
  'scheduledFor',
  'deployedAt',
  'createdAt',
  'updatedAt',
] as const

export type DeploymentSortField = (typeof DEPLOYMENT_SORT_FIELDS)[number]

/**
 * Query params for GET /projects/:projectId/deployments. When `page` or
 * `limit` is present the endpoint returns a `{ data, meta }` paginated
 * envelope; otherwise it returns the legacy plain array.
 */
export interface DeploymentListParams {
  page?: number
  limit?: number
  // Matches name, version, or description (case-insensitive contains)
  search?: string
  environmentId?: string
  status?: DeploymentStatus
  sortBy?: DeploymentSortField
  sortOrder?: 'asc' | 'desc'
  // BaseTable column filter values, JSON-encoded on the wire. Supported keys:
  // name/version (contains), status/environmentId (value list),
  // showInPortal (boolean), scheduledFor/deployedAt/createdAt ({min,max}).
  columnFilters?: Record<string, unknown>
}

// ---- DTO shapes (request bodies) ----

export interface CreateProjectEnvironmentDTO {
  name: string
  key?: string
  color?: string | null
  url?: string | null
  branchName?: string | null
  isProduction?: boolean
  sortOrder?: number
}

export interface UpdateProjectEnvironmentDTO
  extends Partial<CreateProjectEnvironmentDTO> {
  isArchived?: boolean
}

export interface CreateDeploymentDTO {
  environmentId: string
  name: string
  version?: string | null
  description?: string | null
  scheduledFor?: string | null
  ownerId?: string | null
  repositoryId?: string | null
  targetRef?: string | null
  showInPortal?: boolean
}

export interface UpdateDeploymentDTO
  extends Partial<Omit<CreateDeploymentDTO, 'environmentId'>> {
  environmentId?: string
  status?: DeploymentStatus
  releaseNotes?: string | null
  fromSha?: string | null
  toSha?: string | null
  checklist?: DeploymentChecklistItem[]
}

export interface AddDeploymentItemsDTO {
  taskIds?: string[]
  ticketIds?: string[]
}

// Per project-repo GitHub automation (stored in
// ProjectGitHubRepository.automationSettings Json): move linked tasks when
// their PR opens/merges. Values are task status ids for the project.
export interface GitHubRepoAutomationSettings {
  onPrOpenedStatusId?: string | null
  onPrMergedStatusId?: string | null
}

// Project-level deployment automation (stored in Project settings Json under
// `deployments`): move scope tasks when a deployment completes.
export interface ProjectDeploymentAutomationSettings {
  // Per-environment transition status, keyed by ProjectEnvironment.key.
  onDeployedStatusByEnv?: Record<string, string | null>
  // Legacy production-only setting — used as the fallback for production
  // environments when the per-env map has no entry for them.
  onProdDeployedStatusId?: string | null
}

export interface GenerateReleaseNotesResponse {
  releaseNotes: string
}

export interface PromoteDeploymentDTO {
  // Target environment to promote into (must differ from the source's)
  environmentId: string
  // Copy the source deployment's scope items as planned items (default true)
  includeScope?: boolean
}

// ---- Batched lookup: deployment refs for task/ticket table columns ----

export interface DeploymentLookupRequestDTO {
  taskIds?: string[]
  ticketIds?: string[]
}

// Slim reference rendered as a chip in task/ticket tables and detail views
export interface DeploymentRef {
  deploymentId: string
  projectId: string
  name: string
  version?: string | null
  status: DeploymentStatus
  deployedAt?: string | null
  scheduledFor?: string | null
  environment: {
    id: string
    name: string
    key: string
    color?: string | null
    isProduction: boolean
    sortOrder: number
  }
}

export interface DeploymentLookupResponse {
  tasks: Record<string, DeploymentRef[]>
  tickets: Record<string, DeploymentRef[]>
}

// Pick the single most relevant ref for a compact chip: the furthest-along
// DEPLOYED environment wins; otherwise the next upcoming deployment.
export function pickPrimaryDeploymentRef(
  refs: DeploymentRef[],
): DeploymentRef | null {
  if (!refs.length) return null
  const deployed = refs.filter(r => r.status === 'DEPLOYED')
  const pool = deployed.length ? deployed : refs
  return [...pool].sort((a, b) => {
    if (a.environment.isProduction !== b.environment.isProduction) {
      return a.environment.isProduction ? -1 : 1
    }
    if (a.environment.sortOrder !== b.environment.sortOrder) {
      return b.environment.sortOrder - a.environment.sortOrder
    }
    const aDate = a.deployedAt ?? a.scheduledFor ?? ''
    const bDate = b.deployedAt ?? b.scheduledFor ?? ''
    return bDate.localeCompare(aDate)
  })[0]
}

// ---- Semantic version helpers ----

export type VersionBump = 'major' | 'minor' | 'patch'

export interface ParsedVersion {
  major: number
  minor: number
  patch: number
  prefix: string // preserved "v" prefix (or '')
}

// Lenient semver: accepts "1", "1.2", "v1.2.3"; returns null for anything else
export function parseVersion(input: string | null | undefined): ParsedVersion | null {
  if (!input) return null
  const match = input.trim().match(/^(v)?(\d+)(?:\.(\d+))?(?:\.(\d+))?$/i)
  if (!match) return null
  return {
    prefix: match[1] ?? '',
    major: Number(match[2]),
    minor: Number(match[3] ?? 0),
    patch: Number(match[4] ?? 0),
  }
}

export function formatVersion(v: ParsedVersion): string {
  return `${v.prefix}${v.major}.${v.minor}.${v.patch}`
}

export function compareVersions(a: ParsedVersion, b: ParsedVersion): number {
  return a.major - b.major || a.minor - b.minor || a.patch - b.patch
}

export function bumpVersion(
  base: ParsedVersion | null,
  bump: VersionBump,
  // Prefix applied when starting from nothing
  defaultPrefix = 'v',
): string {
  const v = base ?? { major: 0, minor: 0, patch: 0, prefix: defaultPrefix }
  switch (bump) {
    case 'major':
      return formatVersion({ ...v, major: v.major + 1, minor: 0, patch: 0 })
    case 'minor':
      return formatVersion({ ...v, minor: v.minor + 1, patch: 0 })
    case 'patch':
    default:
      return formatVersion({ ...v, patch: v.patch + 1 })
  }
}

// Given all existing version strings in a project, suggest next versions
export function suggestNextVersions(existing: Array<string | null | undefined>): {
  latest: string | null
  major: string
  minor: string
  patch: string
} {
  const parsed = existing
    .map(parseVersion)
    .filter((v): v is ParsedVersion => v !== null)
    .sort(compareVersions)
  const latest = parsed.length ? parsed[parsed.length - 1] : null
  return {
    latest: latest ? formatVersion(latest) : null,
    major: bumpVersion(latest, 'major'),
    minor: bumpVersion(latest, 'minor'),
    patch: bumpVersion(latest, 'patch'),
  }
}

// ---- Portal-facing shapes (slimmed, no internal ids beyond what the UI
// needs; tasks reduced to display facts) ----

export interface PortalDeployment {
  id: string
  projectId: string
  projectName: string
  name: string
  version?: string | null
  description?: string | null
  status: DeploymentStatus
  scheduledFor?: string | null
  deployedAt?: string | null
  releaseNotes?: string | null
  environment: { name: string; key: string; color?: string | null; isProduction: boolean }
  items: Array<{ id: string; title: string; taskNumber?: string | null }>
}
